/* eslint-disable prettier/prettier */
import { FileText, UploadCloud, X } from 'lucide-react';
import { useRef } from 'react';

type FileUploadZoneProps = {
  onFileSelect: (file: File | null) => void;
  noFileError?: boolean;
  setNoFileError: (val: boolean) => void;
  file: File | null;
  setTypeError: (val: boolean) => void;
  setMultiFileError: (val: boolean) => void;
  typeError?: boolean;
  multiFileError?: boolean;
  uploadStatus: 'idle' | 'uploading' | 'error' | 'success';
  duplicateNameError?: boolean;
  setDuplicateNameError: (val: boolean) => void;
  uploadErrorMessage?: string | null;
  onRemoveFile: () => void;
};

export const FileUploadZone = ({
  onFileSelect,
  noFileError,
  setNoFileError,
  file,
  setMultiFileError,
  setTypeError,
  typeError,
  multiFileError,
  uploadStatus,
  duplicateNameError,
  setDuplicateNameError,
  onRemoveFile,
  uploadErrorMessage,
}: FileUploadZoneProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTypeError(false);
    setMultiFileError(false);
    setNoFileError(false);
    setDuplicateNameError(false);

    if (e.target.files) {
      if (e.target.files.length > 1) {
        setMultiFileError(true);
        return;
      }

      const selectedFile = e.target.files[0];
      if (
        selectedFile.type === 'application/pdf' ||
        selectedFile.type === 'application/msword' ||
        selectedFile.type ===
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        onFileSelect(selectedFile);
      } else {
        setTypeError(true);
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setTypeError(false);
    setMultiFileError(false);
    setNoFileError(false);
    setDuplicateNameError(false);

    const files = e.dataTransfer.files;
    if (files.length > 1) {
      setMultiFileError(true);
      return;
    }

    const droppedFile = files[0];
    if (
      droppedFile &&
      (droppedFile.type === 'application/pdf' ||
        droppedFile.type === 'application/msword' ||
        droppedFile.type ===
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
    ) {
      onFileSelect(droppedFile);
    } else {
      setTypeError(true);
    }
  };

  const anyError =
    !!noFileError ||
    !!typeError ||
    !!multiFileError ||
    !!duplicateNameError ||
    uploadStatus === 'error';

  if (file) {
    return (
      <div className="flex flex-col w-full gap-2 mt-7">
        <div
          className={`flex items-center justify-between w-full px-4 py-3 overflow-auto rounded-xl border ${
            anyError ? 'border-red-500' : 'border-secondary'
          }`}
        >
          <div className="flex items-center gap-3 w-[95%]">
            <FileText
              className={`w-6 h-6 font-semibold ${
                anyError ? 'text-red-500' : 'text-primary'
              }`}
            />
            {duplicateNameError ? (
              <input
                type="text"
                value={file.name}
                onChange={(e) => {
                  const renamedFile = new File([file], e.target.value, { type: file.type });
                  onFileSelect(renamedFile);
                }}
                className="w-full text-sm font-semibold text-red-500 bg-transparent border-red-500 focus:outline-none"
              />
            ) : (
              <p className={`text-sm font-semibold truncate ${anyError ? 'text-red-500' : 'text-primary'}`}>
                {file.name}
              </p>
            )}
          </div>
          {(uploadStatus === 'idle' || uploadStatus === 'error') && (
            <button onClick={onRemoveFile}>
              <X
                className={`w-4 h-4 font-extrabold ${anyError ? 'text-red-500' : 'text-primary'}`}
              />
            </button>
          )}
        </div>

        {duplicateNameError && (
          <p className="mb-[-10px] text-sm text-red-500">
            File name already exists. Please modify the name.
          </p>
        )}

        {uploadStatus === 'error' && uploadErrorMessage && (
          <p className="text-sm text-red-500 whitespace-pre-wrap ">{uploadErrorMessage}</p>
        )}
      </div>
    );
  }

  return (
    <div>
      <label
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className={`flex flex-col items-center justify-center gap-1 p-6 text-center transition border-2 border-dashed cursor-pointer rounded-2xl ${
          anyError ? 'border-red-500 mt-7 hover:bg-highlight' : 'border-secondary mt-7 hover:bg-highlight'
        }`}
      >
        <UploadCloud
          className={`w-6 h-6 mb-2 ${anyError ? 'text-red-500' : 'text-primary'}`}
        />
        <p className={`text-sm ${anyError ? 'text-red-500' : 'text-gray-600'}`}>
          Drag and drop file here, or{' '}
          <span className={`underline ${anyError ? 'text-red-500 hover:text-red-500' : 'text-primary hover:text-primary'}`}>
            Choose file
          </span>
        </p>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          accept=".doc, .docx, .pdf"
        />
      </label>

      {noFileError && !file && !typeError && !multiFileError && (
        <p className="mt-1 text-sm text-red-500">Please upload a file</p>
      )}
      {typeError && <p className="mt-1 text-sm text-red-500">The file type should be PDF or DOC</p>}
      {multiFileError && (
        <p className="mt-1 text-sm text-red-500">Please upload only one file at a time</p>
      )}
    </div>
  );
};
